export default {
  title: 'Bank Reconciliation',
  description: 'Match bank statement transactions to payments.',
  previous_month: 'Previous Month',
  next_month: 'Next Month',

  tie_out: {
    title: 'Latest statement balance comparison',
    account_number: 'Account {{number}}',
    account_unknown: 'Account number unavailable',
    as_of: 'As of {{date}}',
    scope: 'Uses the imported statement with the latest end date, regardless of the selected month. Ledger 1120 includes all bank accounts in this FlowRent account; the statement covers the bank account shown above.',
    ledger_balance: 'Ledger balance (1120)',
    statement_balance: 'Statement closing balance',
    difference: 'Difference (ledger − statement)',
    matched: 'Balances match',
    different: 'Balances differ',
    ledger_unavailable: 'The ledger balance is unavailable. These balances cannot be confirmed as matching.',
    opening_missing: 'Opening balance has not been set.',
    set_opening: 'Set opening balance',
    opening_after_statement: 'The opening balance is dated {{date}}, after this statement. It is not included in the ledger balance shown, so this comparison needs review.',
  },

  statements: {
    title: 'Bank Statements',
    description: 'Upload and manage bank statements for reconciliation.',
    new: 'Upload Statement',
    detail: 'Statement Detail',
  },

  reconciliation: {
    title: 'Reconciliation',
    tabs: {
      matched: 'Matched',
      unmatched: 'Unmatched',
      needs_review: 'Needs Review',
      ignored: 'Ignored',
    },
  },

  labels: {
    slip: 'Slip',
    view_slip: 'View payment slip',
    customer: 'Customer',
    invoice: 'Invoice',
    view_payment: 'View payment',
    billing_period: 'Billing Period',
    bank_name: 'Bank',
    start_date: 'Start Date',
    end_date: 'End Date',
    period: 'Period',
    file: 'Statement File',
    file_hint: 'Supports: KBank CSV, SCB & BBL CSV or PDF',
    password: 'Statement Password (if protected)',
    password_placeholder: 'Enter password to decrypt',
    row_count: 'Transactions',
    matched_count: 'Matched',
    unmatched_count: 'Unmatched',
    match_rate: 'Match Rate',
    uploaded_by: 'Uploaded By',
    transaction_date: 'Transaction Date',
    amount: 'Amount',
    reference: 'Reference',
    description: 'Description',
    match_status: 'Match Status',
    matched_payment: 'Matched Payment',
    payment_method: 'Payment Method',
    paid_at: 'Paid At',
    trans_ref: 'Transfer Ref',
    select_month: 'Select Month/Year',
    direction_credit: 'Credit',
    direction_debit: 'Debit',
    net_cash_amount: 'Net Cash',
    expense: 'Expense',
    expense_date: 'Expense Date',
    debit_match_rate: 'Debit Match Rate',
    overlap_warning_title: 'Overlapping statement period',
  },

  banks: {
    scb: 'SCB',
    kbank: 'KBank',
    bbl: 'BBL',
  },

  match_status: {
    unmatched: 'Unmatched',
    auto_matched: 'Auto Matched',
    manual_matched: 'Manual Matched',
    needs_review: 'Needs Review',
    matched_expense: 'Matched Expense',
    // T131-016: terminal, operator-set-aside status — never offered a match.
    ignored: 'Ignored',
  },

  actions: {
    create_expense: 'Create expense',
    upload: 'Upload Statement',
    match: 'Match',
    unmatch: 'Unmatch',
    review: 'Review',
    search_payments: 'Search Payments',
    search_expenses: 'Search Expenses',
    select_payment: 'Select',
    select_expense: 'Select',
    confirm_unmatch: 'Confirm Unmatch',
    // T131-012: mirrors create_expense's placement/wording for the credit side.
    record_payment: 'Record payment',
    // T131-016
    ignore: 'Ignore',
    unignore: 'Unignore',
    confirm_ignore: 'Confirm Ignore',
  },

  // EP-54: i18next v25 plural forms for the candidate-row count badge.
  candidates_count_one: '{{count}} candidate payment',
  candidates_count_other: '{{count}} candidate payments',
  // T131-014: the single-candidate/name-mismatch case newly reachable once
  // T131-013 stopped auto-matching a unique candidate whose name disagrees —
  // worded as "here's a candidate to confirm", not "0 matches" or a failure.
  candidates_count_single_mismatch: "1 possible match, but the name doesn't match — confirm before selecting",

  // T131-009/T131-014: candidate row badges.
  badges: {
    name_match: 'Name match',
    name_mismatch: 'Name mismatch',
  },

  messages: {
    // T131-006: the upload endpoint no longer 422s on an overlapping period —
    // it always imports (deduping row-by-row) and reports what happened.
    // skipped: 0 still names the imported count (never a silent success);
    // skipped > 0 is the normal, healthy outcome of a re-upload and is worded
    // to read that way, not as a failure.
    upload_success_imported_one: 'Imported {{count}} transaction.',
    upload_success_imported_other: 'Imported {{count}} transactions.',
    upload_success_imported_with_skipped_one: 'Imported {{count}} transaction; {{skipped}} already recorded and skipped.',
    upload_success_imported_with_skipped_other: 'Imported {{count}} transactions; {{skipped}} already recorded and skipped.',
    overlap_warning_intro: 'This upload overlaps an existing statement for this bank. Overlapping rows were detected automatically and were not imported twice.',
    match_success: 'Transaction matched successfully.',
    unmatch_success: 'Transaction unmatched successfully.',
    unmatch_confirm: 'Are you sure you want to unmatch this transaction?',
    // T131-016: per-row, operator-driven only — no bulk, no rules.
    ignore_confirm: 'Are you sure you want to ignore this transaction? It will be set aside and never offered a match. You can unignore it later.',
    no_statements: 'No bank statements uploaded yet.',
    no_transactions: 'No transactions found.',
    no_payment_candidates: 'No matching payments found.',
    error_loading: 'Failed to load data.',
    error_description: 'Please try again later.',
    ready_for_accounting: 'All transactions are matched. This month is ready for accounting.',
  },

  errors: {
    wrong_password: 'Wrong password or file is encrypted. Please check and try again.',
    unsupported_format: 'This bank does not support PDF format. Please use CSV.',
    empty_file: 'No transactions found in the uploaded file.',
  },

  placeholders: {
    search_amount: 'Amount',
    search_date: 'Date',
    search_date_from: 'From date',
    search_date_to: 'To date',
    search_reference: 'Reference',
  },

  filters: {
    search: 'Search payments...',
  },

  empty: {
    no_expense_candidates: 'No expense matches this amount. If it has not been recorded yet, create it now.',
    // T131-012: mirrors no_expense_candidates for the credit side — most
    // credit rows genuinely have nothing to match against yet, so the CTA is
    // the normal next step, not an error state.
    no_payment_candidates: 'No matching payment found yet. If it has not been recorded, record it now.',
    statements: 'No bank statements uploaded yet.',
    transactions: 'No transactions in this category.',
    payment_search: 'No matching payments found.',
  },

  summary: {
    total: 'Total',
    matched: 'Matched',
    unmatched: 'Unmatched',
    needs_review: 'Needs Review',
    match_rate: 'Match Rate',
    credit_match_rate: 'Credit Match Rate',
    debit_match_rate: 'Debit Match Rate',
  },

  tabs: {
    transactions: 'Transactions',
    statements: 'Statements',
    // EP-143 T143-016: admin+ only tab
    slip_recon: 'Slip recon',
    view: 'View',
    all: 'All',
    // EP-97: default work-queue sub-tab (unmatched + needs_review).
    needs_attention: 'Needs Attention',
  },

  // EP-143 T143-016: Slip recon tab strings
  slip_recon: {
    empty_title: 'No recon candidates',
    empty_description: 'Verified payments that cannot be matched to a bank credit will appear here.',
    col_paid_at: 'Paid At',
    col_amount: 'Amount',
    col_customer: 'Customer',
    col_slip_uploaded_at: 'Slip uploaded',
    col_gate_summary: 'Gate summary',
    flag_fraud: 'Flag as fraud',
    flag_success: 'Payment flagged as fraud.',
    ml_outcome: {
      ref1_exact: 'Ref1 exact',
      amount_trans_ref: 'Amount + ref',
      none: 'No match',
      // EP-145 A5: the EP-143 rental outcomes. Without these the chip rendered
      // a raw key for every rental slip in this tab.
      ml_agree: 'ML agrees',
      ml_disagree: 'ML disagrees',
      ml_solo_verify: 'ML alone',
      ml_pass_rdcw_fail: 'ML passed, bank rejected',
    },
    orphan_section_title: 'Unmatched bank credits',
    orphan_section_description: 'Bank credits with no matching payment can be found in the Transactions tab under "Needs Attention".',
    orphan_section_link: 'View unmatched credits →',
  },

  // EP-100: Stripe payout reconciliation
  stripe_payout: {
    title: 'Stripe Payouts',
    description: 'Import Stripe itemized payout reconciliation to match bank deposits and post fees to the ledger.',
    upload_label: 'Stripe Itemized Payout CSV',
    upload_hint: 'Upload the Itemized Payout Reconciliation CSV exported from Stripe.',
    upload_action: 'Import Stripe Payout',
    import_result_title: 'Payout Import Result',
    payout_id: 'Payout ID',
    gross: 'Gross',
    fee: 'Processing Fee',
    net: 'Net Deposited',
    matched_deposit: 'Matched Bank Deposit',
    arrival_date: 'Arrival Date',
    currency: 'Currency',
    fee_posted: 'Fee Posted to GL',
    fee_not_posted: 'Fee Not Posted (GL Off)',
    matched: 'Matched',
    unmatched: 'Unmatched',
    import_success_summary: 'Imported {{imported}} payout(s), {{matched}} matched to a bank row',
    import_success: 'Stripe payout imported successfully.',
    import_error: 'Failed to import Stripe payout CSV.',
    duplicate_payout: 'This payout has already been imported.',
  },
}
